// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { xCommonUpdate } from "@vanmeter/App/Handlers/XNG/Common";
import { xIsAuthenticated, xIsGuest } from "@vanmeter/App/Store/Selectors/GeneralSelectors";
import { addToCartStatus } from "@vanmeter/XDL/constants";
import Check from "@vanmeter/XDL/Icons/IconCheck";
import { parseBoolean } from "@vanmeter/XDL/Utilities";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    onAddToCartClick?: () => void;
    iconOnly?: boolean;
}

// <XNG-Changes>
export interface IconConfig {
    src: React.ComponentType | string;
    position: "right" | "left";
}
// </XNG-Changes>

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: ApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
        accountSettings: { allowGuestCheckout },
    } = getSettingsCollection(state);

    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
            props.configurationSelection,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        // XNG-Changes
        isAuthenticated: xIsAuthenticated(state),
        isGuest: xIsGuest(state),
        allowGuestCheckout,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
    openSignInModal: () => xCommonUpdate({ key: "isSignInModalOpen", value: true }),
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {};
export const productAddToCartIconButtonStyles: ButtonPresentationProps = {};

const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId, allowZeroPricing, quoteRequired, properties, productTitle },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory, failedToLoadPricing },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    iconOnly,
    // XNG-Changes
    openSignInModal,
    isAuthenticated,
    isGuest,
    allowGuestCheckout,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));
    const [iconButtonStyles] = useState(() => mergeToNew(productAddToCartIconButtonStyles, extendedStyles));
    const [status, setStatus] = useState(addToCartStatus.default);

    // XNG-Changes: button should be disabled for productCantBuy
    const productCantBuy = parseBoolean(properties.cantBuy);

    useEffect(() => {
        if (status === addToCartStatus.success) {
            setTimeout(() => {
                setStatus(addToCartStatus.default);
            }, 800);
        }
    }, [status]);

    const iconConfig: IconConfig = {
        src: status === addToCartStatus.success ? Check : "",
        position: "right",
        ...{ height: 14, width: 16 },
    };
    // <XNG-Changes>

    // if ((!hasEnoughInventory && !failedToLoadInventory) || !canAddToCart) {
    //     return null;
    // }

    const addToCartClickHandler = async () => {
        // XNG-Changes: VM - Classic & Design
        if (!isAuthenticated && !allowGuestCheckout) {
            openSignInModal();
            return;
        }

        onAddToCartClick?.();

        // XNG-Changes: match classic for adding 0 priced products
        // if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
        //     toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
        //     return;
        // }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;
        // <XNG-Changes>
        setStatus(addToCartStatus.loading);
        // <XNG-Changes>

        const cartline = (await addToCart({
            productId,
            qtyOrdered,
            unitOfMeasure,
            notes,
            sectionOptions,
            subscription,
        })) as CartLineModel;

        // <XNG-Changes>
        setStatus(addToCartStatus.success);
        // <XNG-Changes>

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
                timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
            });
        }
    };

    const isDisabled =
        qtyOrdered <= 0 ||
        isAddToCartButtonDisabled ||
        productCantBuy ||
        (!hasEnoughInventory && !failedToLoadInventory) ||
        !canAddToCart;
    /**
     * XNG-Changes
     * to prevent button width change when the status change, we use position absolute and text-color: transparent
     */
    if (iconOnly) {
        return (
            <Button
                {...iconButtonStyles}
                onClick={addToCartClickHandler}
                className={`button-wrapper-icon-only ${status.status}`}
                disabled={isDisabled}
                icon={iconConfig}
                {...otherProps}
                aria-label="add to cart icon button"
                {...{ isLoading: status === addToCartStatus.loading }}
            ></Button>
        );
    }

    return (
        <Button
            {...styles}
            onClick={addToCartClickHandler}
            disabled={isDisabled || (isGuest && parseBoolean(properties?.guestViewOnly))}
            {...otherProps}
            // XNG-Changes
            {...{ isLoading: status === addToCartStatus.loading }}
            aria-label={labelOverride ?? translate("Add to Cart")}
        >
            {labelOverride ?? translate("Add to Cart")}
        </Button>
    );
};
export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
